import { cleanOutlineWord } from '@/lib/public/effects/catalogue';
import { hashString, seededRandom } from '@/lib/public/effects/seed';

/**
 * The geometry behind Sprint, Halftone, Chalk and Outline type. Pure and
 * seeded, so each event draws the same every visit and tests need no browser.
 */

// ── Sprint ──────────────────────────────────────────────────────────────────

export interface Streak {
  /** Percent of the streak box. */
  top: number;
  left: number;
  width: number;
  /** CSS pixels. */
  height: number;
  /** 0 to 1, inside the layer. */
  opacity: number;
  /** Seconds. */
  duration: number;
  /** Seconds, negative so the streaks are already mid-flight on arrival. */
  delay: number;
}

/**
 * Fourteen streaks, mostly towards the right and the top, where the effect
 * lives; where they rest is where reduced motion leaves them, so the still
 * version is spread out and looks finished.
 */
export function sprintStreaks(seed: string, count = 14): Streak[] {
  const r = seededRandom(hashString(`sprint:${seed}`));
  const out: Streak[] = [];
  for (let i = 0; i < count; i++) {
    // Stratified down the band the layer shows (the box overhangs it), so no two bunch up.
    const top = 18 + ((i + 0.15 + r() * 0.7) / count) * 62;
    out.push({
      top: round1(top),
      left: round1(30 + r() * 60),
      width: round1(12 + r() * 24),
      height: r() < 0.3 ? 2 : 1,
      opacity: round2(0.55 + r() * 0.45),
      duration: round1(5 + r() * 6),
      delay: round1(-r() * 10),
    });
  }
  return out;
}

// ── Halftone ────────────────────────────────────────────────────────────────

/**
 * Print dots on a hex grid, largest in the top-right corner and shrinking to
 * nothing towards the text. x, y and radius triples in CSS pixels.
 */
export function halftoneDots(w: number, h: number, spacing = 10, maxRadius = spacing * 0.48): Float32Array {
  const out: number[] = [];
  const reach = Math.hypot(w, h) * 0.62;
  const cx = w * 1.02;
  const cy = -h * 0.04;
  const rowGap = spacing * 0.87;
  for (let y = 0, row = 0; y < h + spacing; y += rowGap, row++) {
    for (let x = (row % 2) * (spacing / 2); x < w + spacing; x += spacing) {
      const t = 1 - Math.hypot(cx - x, cy - y) / reach;
      if (t <= 0.05) continue;
      out.push(x, y, maxRadius * Math.pow(t, 1.5));
    }
  }
  return Float32Array.from(out);
}

// ── Chalk ───────────────────────────────────────────────────────────────────

export interface ChalkLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  alpha: number;
}

export interface ChalkDrawing {
  /** Soft dust patches: x, y, radius, alpha. */
  smudges: Array<{ x: number; y: number; r: number; alpha: number }>;
  lines: ChalkLine[];
}

/**
 * Chalk dust and three tallies of five (four strokes and a slash), with an
 * underline, in the top right: a whiteboard after a workout. Each stroke is
 * six rough passes, so it reads as chalk rather than a vector line.
 */
export function chalkDrawing(seed: string, w: number, h: number): ChalkDrawing {
  const r = seededRandom(hashString(`chalk:${seed}`));
  const smudges = [];
  for (let m = 0; m < 4; m++) {
    smudges.push({ x: w * (0.45 + r() * 0.55), y: h * r() * 0.7, r: 60 + r() * 120, alpha: 0.12 + r() * 0.08 });
  }
  const sc = Math.min(1.25, Math.max(0.22, w / 520));
  const bx = w - (3 * 74 + 30) * sc;
  const by = Math.min(h * 0.14, 90);
  const cos = Math.cos(-0.05);
  const sin = Math.sin(-0.05);
  const place = (x: number, y: number): [number, number] => [bx + (x * cos - y * sin) * sc, by + (x * sin + y * cos) * sc];
  const lines: ChalkLine[] = [];
  const stroke = (x1: number, y1: number, x2: number, y2: number) => {
    for (let k = 0; k < 6; k++) {
      const [ax, ay] = place(x1 + (r() - 0.5) * 3, y1 + (r() - 0.5) * 3);
      const [bx2, by2] = place(x2 + (r() - 0.5) * 3, y2 + (r() - 0.5) * 3);
      lines.push({ x1: ax, y1: ay, x2: bx2, y2: by2, width: (0.7 + r() * 1.6) * Math.max(0.6, sc), alpha: 0.3 + r() * 0.45 });
    }
  };
  for (let g = 0; g < 3; g++) {
    const ox = g * 74;
    for (let v = 0; v < 4; v++) {
      const vx = ox + v * 12;
      stroke(vx, 0, vx + 2, 48);
    }
    stroke(ox - 8, 42, ox + 46, 6);
  }
  stroke(0, 78, 210, 72);
  stroke(4, 86, 150, 84);
  return { smudges, lines };
}

/** Specks for the chalk-dust tile: x, y, size, alpha, in a size x size tile. */
export function chalkGrain(size = 180, count = 1100): Float32Array {
  const r = seededRandom(7);
  const out = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    out[i * 4] = r() * size;
    out[i * 4 + 1] = r() * size;
    out[i * 4 + 2] = 0.5 + r() * 1.2;
    out[i * 4 + 3] = 0.1 + r() * 0.3;
  }
  return out;
}

// ── Outline type ────────────────────────────────────────────────────────────

const STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'and', 'at', 'in', 'on', 'for', 'by', 'to']);

/**
 * The word Outline type draws, from the event itself:
 *   1. a distance or format in the name, the way athletes say it (10K, 5K,
 *      8x1K, HM), or a year written in the name (2026);
 *   2. otherwise the year the event is on, from its start date;
 *   3. otherwise the initials of the name (up to three letters).
 * A year reads well in any heading font and every event has one, which is
 * why it comes before initials (which can spell something odd).
 */
export function outlineWord(eventName: string | null | undefined, startDate?: string | Date | null, timeZone?: string | null): string {
  const name = (eventName ?? '').trim();
  const token = name.match(/\b(\d+(?:[.,]\d+)?\s?(?:k|km|mi|m)\b|\d+\s?[x×]\s?\d+\s?k?m?\b|(?:19|20)\d{2}\b|hm\b|ultra\b)/i);
  if (token) return token[1].replace(/\s+/g, '').toUpperCase().replace('X', '×');
  const d = startDate ? new Date(startDate) : null;
  if (d && !Number.isNaN(d.getTime())) return eventYear(d, timeZone);
  const initials = name
    .split(/[\s\-–/]+/)
    .filter((w) => w && !STOP_WORDS.has(w.toLowerCase()) && /[\p{L}\d]/u.test(w))
    .map((w) => (w.match(/[\p{L}\d]/u)?.[0] ?? '').toUpperCase())
    .join('')
    .slice(0, 3);
  return initials || 'GO';
}

/**
 * The word Outline type draws: the organiser's own (landingThemeOutlineWord)
 * when they typed one, cleaned as the route stores it; otherwise the
 * automatic word above.
 */
export function outlineWordFor(
  custom: string | null | undefined,
  eventName: string | null | undefined,
  startDate?: string | Date | null,
  timeZone?: string | null,
): string {
  return cleanOutlineWord(custom) || outlineWord(eventName, startDate, timeZone);
}

/** The year in the event's own timezone (an event at 00:30 on New Year's Day is next year's). */
function eventYear(d: Date, timeZone?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: timeZone || 'UTC' }).format(d);
  } catch {
    return String(d.getUTCFullYear());
  }
}

// ── Heading font ────────────────────────────────────────────────────────────

/**
 * The CSS font-family for headings, as ThemedEventPage works it out, for
 * the picker's previews (the public page reads --fv-heading-font instead).
 */
export function headingFontFamily(fontStyle: string | null | undefined, customHeading?: string | null): string {
  if (fontStyle === 'custom' && customHeading) return `'${customHeading.replace(/['"\\;{}<>]/g, '')}', sans-serif`;
  switch (fontStyle) {
    case 'modern':
      return 'var(--font-caprasimo), serif';
    case 'bold':
      return 'var(--font-bebas-neue), Impact, sans-serif';
    case 'minimal':
      return 'var(--font-inter), sans-serif';
    default:
      return 'Arial, Helvetica, sans-serif';
  }
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;
